import React from 'react';
import { CardGameType, ArenaGameState } from '../state/ArenaTypes';
import { GameTableConfig } from '../engine/tableConfig/types';
import { GameHudProps, GamePresentation, UNSUPPORTED_PRESENTATION } from '../presentation/GamePresentationTypes';
import {
  IGameModule,
  GameTerminology,
  GameLabels,
  GameIcons,
  GameDisplayIndicators,
  GamePresentationResult
} from './types';
import { bidWhistModule } from './BidWhist/BidWhistModule';
import { spadesModule } from './Spades/SpadesModule';
import { heartsModule } from './Hearts/HeartsModule';
import { StandardHandSortingStrategy } from './common/StandardHandSortingStrategy';
import { IScorePresenter } from './common/IScorePresenter';
import { IContractFormatterStrategy } from './common/IContractFormatterStrategy';

export { UNSUPPORTED_PRESENTATION };

/**
 * Fallback game module for explicit but unknown / unsupported game types.
 * Prevents unknown game types from silently masquerading as Bid Whist.
 */
export class UnsupportedGameModule implements IGameModule {
  public readonly gameType: CardGameType = 'UNSUPPORTED' as CardGameType;
  public readonly displayName: string = 'Unsupported Game';
  public readonly aliases: readonly string[] = [];
  public readonly terminology: GameTerminology = {
    trickNoun: 'trick',
    trickNounPlural: 'tricks',
    bidNoun: 'bid',
    trumpNoun: 'trump',
    sweepNoun: 'sweep',
    penaltyNoun: 'penalty'
  };
  public readonly labels: GameLabels = {
    displayName: 'Unsupported Game',
    scoreboardTitle: 'Scoreboard',
    team1Label: 'Team 1',
    team2Label: 'Team 2',
    winningBidLabel: 'Bid',
    contractSummaryLabel: 'Contract',
    phaseDisplayNames: {}
  };
  public readonly icons: GameIcons = {
    themeSymbol: '🃏',
    themeColor: 'emerald',
    iconType: 'shield'
  };
  public readonly scorePresenter: IScorePresenter = {
    formatScoreDisplay: (state) => ({
      team1Label: String(state?.team1Score ?? 0),
      team2Label: String(state?.team2Score ?? 0),
      targetScoreLabel: ''
    }),
    calculateBooksToSet: () => ({
      booksNeededToSet: 0,
      isSet: false,
      booksRemaining: 0,
      targetBooks: 0
    })
  };
  public readonly contractFormatter: IContractFormatterStrategy = {
    formatContractName: () => 'Standard Contract',
    formatContractOutcome: () => '',
    getContractSummaryText: () => null,
    getHighestBidRecord: () => null
  };
  public readonly handSortingStrategy = new StandardHandSortingStrategy();
  public readonly presentation: GamePresentation = UNSUPPORTED_PRESENTATION;

  public getPresentation(state: ArenaGameState): GamePresentationResult {
    return {
      ...this.presentation,
      contractSummary: null,
      outcomeText: null,
      indicators: this.getDisplayIndicators(state)
    };
  }

  public getDisplayIndicators(state: ArenaGameState): GameDisplayIndicators {
    return {
      trickWinnerSeat: state?.trickWinnerSeat ?? null,
      isGameOver: state?.phase === 'GAME_OVER',
      matchWinner: null,
      leadSuit: null,
      currentTurnSeat: state?.currentTurnSeat ?? null
    };
  }

  public formatContract(): string {
    return '';
  }

  public formatOutcome(): string {
    return '';
  }

  public mapRealtimeState(raw: unknown): Record<string, unknown> {
    if (typeof raw !== 'object' || raw === null || Array.isArray(raw)) {
      return {};
    }
    return { ...(raw as Record<string, unknown>) };
  }

  public getBotTurn(): null {
    return null;
  }

  public getBotTurnDelayMs(): number {
    return 0;
  }

  public getLastActorSeat(): null {
    return null;
  }
}

export const unsupportedGameModule: IGameModule = new UnsupportedGameModule();

export const MODULE_REGISTRY: Map<string, IGameModule> = new Map();

/**
 * Register a game module and all its associated aliases.
 * Rejects conflicting registrations to prevent aliases from colliding across modules.
 */
export function registerGameModule(moduleOrType: IGameModule | string, moduleInstance?: IGameModule): void {
  if (typeof moduleOrType === 'string' && moduleInstance) {
    const key = moduleOrType.trim().toUpperCase();
    const existing = MODULE_REGISTRY.get(key);
    if (existing && existing !== moduleInstance) {
      throw new Error(`Cannot register game module for '${key}': already registered to '${existing.displayName || existing.gameType}'.`);
    }
    MODULE_REGISTRY.set(key, moduleInstance);

    if (moduleInstance.gameType) {
      const canonicalKey = moduleInstance.gameType.trim().toUpperCase();
      const existingCanonical = MODULE_REGISTRY.get(canonicalKey);
      if (existingCanonical && existingCanonical !== moduleInstance) {
        throw new Error(`Cannot register game module for '${canonicalKey}': already registered to '${existingCanonical.displayName || existingCanonical.gameType}'.`);
      }
      MODULE_REGISTRY.set(canonicalKey, moduleInstance);
    }
    if (moduleInstance.aliases) {
      for (const alias of moduleInstance.aliases) {
        const aliasKey = alias.trim().toUpperCase();
        const existingAlias = MODULE_REGISTRY.get(aliasKey);
        if (existingAlias && existingAlias !== moduleInstance) {
          throw new Error(`Cannot register alias '${aliasKey}' for '${moduleInstance.gameType}': already registered to '${existingAlias.displayName || existingAlias.gameType}'.`);
        }
        MODULE_REGISTRY.set(aliasKey, moduleInstance);
      }
    }
    return;
  }

  const module = moduleOrType as IGameModule;
  const primaryKey = module.gameType.trim().toUpperCase();
  const existingPrimary = MODULE_REGISTRY.get(primaryKey);
  if (existingPrimary && existingPrimary !== module) {
    throw new Error(`Cannot register game module for '${primaryKey}': already registered to '${existingPrimary.displayName || existingPrimary.gameType}'.`);
  }
  MODULE_REGISTRY.set(primaryKey, module);

  if (module.aliases) {
    for (const alias of module.aliases) {
      const aliasKey = alias.trim().toUpperCase();
      const existingAlias = MODULE_REGISTRY.get(aliasKey);
      if (existingAlias && existingAlias !== module) {
        throw new Error(`Cannot register alias '${aliasKey}' for '${module.gameType}': already registered to '${existingAlias.displayName || existingAlias.gameType}'.`);
      }
      MODULE_REGISTRY.set(aliasKey, module);
    }
  }
}

// Register standard game modules and their aliases
registerGameModule(bidWhistModule);
registerGameModule(spadesModule);
registerGameModule(heartsModule);

/**
 * Resolves any game type alias (e.g. '500_PTS', 'SEVEN_IN_OUT', '100_PTS')
 * to its canonical gameType ('SPADES', 'BID_WHIST', 'HEARTS').
 * Preserves unknown values instead of silently converting them to Bid Whist.
 * Only defaults to Bid Whist when no game type is provided at all.
 */
export function resolveCanonicalGameType(target?: string | null): string {
  if (!target || !target.trim()) {
    return bidWhistModule.gameType;
  }
  const norm = target.trim().toUpperCase();
  const module = MODULE_REGISTRY.get(norm);
  if (module) {
    return module.gameType;
  }
  return norm;
}

/**
 * Returns all configured aliases for a given canonical game type.
 */
export function getAliasesForGameType(gameType?: string | null): string[] {
  if (!gameType || !gameType.trim()) return [];
  const norm = gameType.trim().toUpperCase();
  const module = MODULE_REGISTRY.get(norm);
  return module?.aliases ? [...module.aliases] : [];
}

/**
 * Returns all registered client game modules without duplicates,
 * derived dynamically from MODULE_REGISTRY.
 */
export function getAllGameModules(): IGameModule[] {
  return Array.from(new Set(MODULE_REGISTRY.values()));
}

/**
 * Resolves the client Game Module strictly based on canonical gameType and registered aliases.
 * Table features (hasPassingPhase, contractFormat, hasKitty, hasDirectionBidding, biddingModel)
 * are rule configurations, NOT game identity, and must never be used to infer the game.
 *
 * Behavior:
 * - If target or target.gameType is omitted/empty, defaults to Bid Whist.
 * - Resolves canonical game types and aliases through MODULE_REGISTRY.
 * - If an explicit but unknown game type is provided, logs a warning and returns unsupportedGameModule fallback.
 */
export function getGameModule(target?: GameTableConfig | CardGameType | string | null): IGameModule {
  if (target === undefined || target === null) {
    return bidWhistModule;
  }

  let rawGameType: string | undefined;

  if (typeof target === 'object') {
    rawGameType = target.gameType;
  } else {
    rawGameType = target;
  }

  if (rawGameType === undefined || rawGameType === null || rawGameType.trim() === '') {
    return bidWhistModule;
  }

  const norm = rawGameType.trim().toUpperCase();
  const module = MODULE_REGISTRY.get(norm);
  if (module) {
    return module;
  }

  console.warn(`[GameModuleManager] Unknown or unregistered game type: "${rawGameType}". Returning unsupported game module fallback.`);
  return unsupportedGameModule;
}
